<script setup>
import { onMounted, reactive, ref } from 'vue';
import { Button, Card, Select, Space, Table, Tag, message } from 'ant-design-vue';
import { getOperationLogMeta, getOperationLogs } from '@/api/audit/operationLogs';
import { usePermission } from '@/composables/usePermission';
import formatDateTime from '@/utils/formatDateTime';

const { canView } = usePermission('OperationLog');

const loading = ref(false);
const rows = ref([]);
const total = ref(0);
const page = ref(1);
const pageSize = ref(20);
const moduleOptions = ref([]);
const operatorOptions = ref([]);
const filters = reactive({
    module: undefined,
    manager_id: undefined
});

const actionColor = (action) => {
    if (action === 'created') return 'success';
    if (action === 'deleted') return 'error';
    return 'processing';
};

const columns = [
    { title: '時間', dataIndex: 'created_at', key: 'created_at', width: 170 },
    { title: '項目', dataIndex: 'module_label', key: 'module_label', width: 150 },
    { title: '操作', key: 'action', width: 88 },
    { title: '操作者', dataIndex: 'manager_name', key: 'manager_name', width: 140 },
    { title: '摘要', dataIndex: 'summary', key: 'summary', ellipsis: true }
];

const loadMeta = async () => {
    try {
        const res = await getOperationLogMeta();
        if (res.status !== 1) throw new Error(res.message || '載入篩選失敗');
        moduleOptions.value = (res.data?.modules || []).map((item) => ({
            value: item.value,
            label: item.label
        }));
        operatorOptions.value = (res.data?.operators || []).map((item) => ({
            value: item.id,
            label: item.name
        }));
    } catch (e) {
        message.error(e.message || '載入篩選失敗');
    }
};

const load = async (p = page.value) => {
    if (!canView.value) return;
    loading.value = true;
    try {
        const res = await getOperationLogs({
            page: p,
            page_size: pageSize.value,
            module: filters.module || undefined,
            manager_id: filters.manager_id || undefined
        });
        if (res.status !== 1) throw new Error(res.message || '載入失敗');
        rows.value = res.data || [];
        total.value = res.total || 0;
        page.value = res.page || p;
        pageSize.value = res.page_size || pageSize.value;
    } catch (e) {
        message.error(e.message || '載入失敗');
    } finally {
        loading.value = false;
    }
};

const onSearch = () => {
    page.value = 1;
    load(1);
};

const onReset = () => {
    filters.module = undefined;
    filters.manager_id = undefined;
    onSearch();
};

const onTableChange = (pag) => {
    page.value = pag.current;
    pageSize.value = pag.pageSize;
    load(pag.current);
};

onMounted(async () => {
    if (!canView.value) return;
    await loadMeta();
    await load(1);
});
</script>

<template>
    <div class="operation-log-page pb-10">
        <p v-if="!canView" class="text-slate-500">無檢視權限</p>
        <template v-else>
            <Card
                :bordered="false"
                class="mb-4 shadow-sm rounded-2xl border border-emerald-100/50 bg-white"
            >
                <p class="text-sm text-[#5c6670] m-0 mb-3 leading-relaxed">
                    記錄後台新增／編輯／刪除操作。可用項目與操作者篩選。
                </p>
                <div class="flex flex-col sm:flex-row gap-3 sm:items-center">
                    <Select
                        v-model:value="filters.module"
                        allow-clear
                        placeholder="項目"
                        :options="moduleOptions"
                        class="w-full sm:w-[220px]"
                    />
                    <Select
                        v-model:value="filters.manager_id"
                        allow-clear
                        show-search
                        option-filter-prop="label"
                        placeholder="操作者"
                        :options="operatorOptions"
                        class="w-full sm:w-[220px]"
                    />
                    <Space>
                        <Button type="primary" @click="onSearch">查詢</Button>
                        <Button @click="onReset">重置</Button>
                    </Space>
                </div>
            </Card>
            <Card :bordered="false" class="shadow-sm rounded-2xl">
                <Table
                    row-key="id"
                    :columns="columns"
                    :data-source="rows"
                    :loading="loading"
                    :pagination="{
                        current: page,
                        pageSize,
                        total,
                        showSizeChanger: true,
                        showTotal: (n) => `共 ${n} 筆`
                    }"
                    @change="onTableChange"
                >
                    <template #bodyCell="{ column, record }">
                        <template v-if="column.key === 'created_at'">
                            {{ formatDateTime(record.created_at) }}
                        </template>
                        <template v-else-if="column.key === 'action'">
                            <Tag :color="actionColor(record.action)">
                                {{ record.action_label || record.action }}
                            </Tag>
                        </template>
                        <template v-else-if="column.key === 'manager_name'">
                            {{ record.manager_name || '—' }}
                        </template>
                    </template>
                </Table>
            </Card>
        </template>
    </div>
</template>
